import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { Proposal } from '@shared/types';
import { getProposal } from '../api/client';
import { PageFrame } from '../pages/Page';
import { PageArchetype } from '../pages/registry';
import { READ_ONLY_ACTIONS } from '../pages/types';
import { visibleSections } from '../lib/plan';
import { themeStyle } from '../lib/theme';

/**
 * /print/:id - every visible page rendered sequentially through the same
 * page components as the workspace, one PDF page per proposal page. Print CSS
 * (`@page { size: 13.333in 7.5in; margin: 0 }`, `break-after: page`) makes
 * this the PDF body Stream J's server route renders headlessly.
 * `?print=1` opens the browser's print dialog once the document has rendered.
 */
export function PrintView({
  proposalId,
  autoPrint,
}: {
  proposalId: string;
  autoPrint: boolean;
}): ReactElement {
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('printing');
    return () => document.body.classList.remove('printing');
  }, []);

  useEffect(() => {
    let live = true;
    void getProposal(proposalId)
      .then((res) => {
        if (live) setProposal(res.proposal);
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, [proposalId]);

  useEffect(() => {
    if (!proposal) return;
    const name = proposal.advertiser.name;
    document.title = name ? `${name} — Media Proposal` : 'Media Proposal';
  }, [proposal]);

  useEffect(() => {
    if (!proposal || !autoPrint) return;
    // One frame for layout, then hand off to the browser.
    const timer = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(timer);
  }, [proposal, autoPrint]);

  if (error) return <p className="loading">{error}</p>;
  if (!proposal) return <p className="loading">Preparing document…</p>;
  if (!proposal.document) {
    return <p className="loading">This proposal has no document yet.</p>;
  }

  const sections = visibleSections(proposal);

  return (
    // paper-print is the readiness marker src/server/pdf.ts waits for — keep it.
    <div className="print-pages paper-print" style={themeStyle(proposal.document.theme)}>
      {sections.map((section) => (
        <PageFrame key={section.id} className="print-page">
          <PageArchetype proposal={proposal} section={section} actions={READ_ONLY_ACTIONS} readOnly />
        </PageFrame>
      ))}
    </div>
  );
}
